/** PostHog-owned analytics events that receive the context fields. */
export const POSTHOG_ENVELOPE_EVENT_NAMES = {
  PAGEVIEW: '$pageview',
  PAGELEAVE: '$pageleave',
  AUTOCAPTURE: '$autocapture',
  EXCEPTION: '$exception'
} as const;

/** PostHog transport/recording events that must not receive analytics context. */
export const POSTHOG_INTERNAL_EVENT_NAMES = {
  SNAPSHOT: '$snapshot'
} as const;

/** PostHog group analytics names the send boundary reads and rewrites. */
export const POSTHOG_GROUP_NAMES = {
  /** The event `group()` sends to update a group's profile. */
  GROUP_IDENTIFY: '$groupidentify',
  /** Event property holding `{ groupType: groupKey }` for every group an event counts toward. */
  GROUPS: '$groups',
  GROUP_TYPE: '$group_type',
  GROUP_KEY: '$group_key'
} as const;

/**
 * The two events posthog-js can emit for a single `identify()` call. Which one
 * fires is decided by the SDK's persisted `$user_state`: an anonymous client
 * transitioning to a known user emits `$identify` (carrying `$anon_distinct_id`,
 * the instruction that merges the anonymous journey into the person); a client
 * already marked identified re-sends the same person properties as `$set`.
 * Both carry the properties in a `$set` payload, so email is a PERSON property
 * on either path and never an event property.
 */
export const POSTHOG_PERSON_PROPERTY_EVENT_NAMES = {
  IDENTIFY: '$identify',
  SET: '$set'
} as const;

/** PostHog Error Tracking property names used by the envelope send boundary. */
export const POSTHOG_EXCEPTION_PROPERTY_NAMES = {
  // The exception-Issue grouping key; PostHog renders the bold Issue title from
  // TYPE and the body from MESSAGE, so tests assert on these exact spellings.
  FINGERPRINT: '$exception_fingerprint',
  LEVEL: '$exception_level',
  LIST: '$exception_list',
  MESSAGE: '$exception_message',
  TYPE: '$exception_type',
  // Which subsystem raised the failure. Custom error events set this so an
  // exception-tracking query can split by producer without matching event names.
  SOURCE: '$exception_source'
} as const;

/**
 * Crash class the renderer stamps on `$exception_level` (noise-filter.ts).
 * Catalog `S1/fatal` projects to FATAL; everything else is ERROR. Electron main
 * (posthog-main.ts) and the encoder forwarder (encoder-errors.ts) do not import
 * this; each spells the same two values itself.
 */
export const POSTHOG_EXCEPTION_LEVEL = {
  FATAL: 'fatal',
  ERROR: 'error'
} as const;

export type PostHogExceptionLevel =
  (typeof POSTHOG_EXCEPTION_LEVEL)[keyof typeof POSTHOG_EXCEPTION_LEVEL];

/** Analytics-relevant PostHog events that bypass the renderer track() path. */
export const POSTHOG_ENVELOPE_BYPASS_EVENTS: ReadonlySet<string> = new Set([
  POSTHOG_ENVELOPE_EVENT_NAMES.PAGEVIEW,
  POSTHOG_ENVELOPE_EVENT_NAMES.PAGELEAVE,
  POSTHOG_ENVELOPE_EVENT_NAMES.AUTOCAPTURE,
  POSTHOG_ENVELOPE_EVENT_NAMES.EXCEPTION
]);

/**
 * Enrich named events and the explicit `$` allowlist, but not transport internals.
 * Lives here rather than beside its only production caller so a test can ask the
 * SAME question production asks — "would the analytics context land on this
 * event?" — without pulling in that module's unresolvable-from-e2e dependencies.
 */
export function shouldEnrichEnvelope(eventName: string): boolean {
  return !eventName.startsWith('$') || POSTHOG_ENVELOPE_BYPASS_EVENTS.has(eventName);
}

/** PostHog SDK/ingest property names used by telemetry assertions. */
export const POSTHOG_EVENT_PROPERTY_NAMES = {
  CURRENT_URL: '$current_url',
  DISTINCT_ID: 'distinct_id',
  API_KEY: 'api_key'
} as const;
